// @flow
import React, { Component } from 'react'
import Helmet from 'react-helmet'
import { connect } from 'react-redux'
import styled from 'styled-components'
import get from 'lodash/get'
import { User } from '../../types'
import { alert } from '../../actions/alert'
import { fetchProperties } from '../../actions/properties'
import Pager from '../../components/Pager'
import Button from '../../components/Button'
import LinkButton from '../../components/LinkButton'
import SearchResult from './components/SearchResult'
import LoadingResult from './components/LoadingResult'
import LoadingOverlay from './components/LoadingOverlay'
import Filter from './components/Filter'
import NoResults from './components/NoResults'
import AlertFormModal from '../../components/AlertFormModal'
import {
  minPriceOptions,
  maxPriceOptions,
  minAreaOptions,
  sortOptions,
  minBedOptions,
  minBathOptions
  // typeOptions
} from '../../data/filters'
import debounce from 'lodash/debounce'

const Header = styled.h1`
  text-align: center;
  margin-bottom: 1rem;
`

const FiltersContainer = styled.div`
  background: #fff;
  padding: 4rem 0;
  border-bottom: 1px solid #e1e6eb;
  margin-bottom: 2rem;
`

const ResultsContainer = styled.div`
  padding: 1rem 0;
`

const Results = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const SaveSearchTop = styled.p`
  position: relative;
  text-align: center;
  margin-bottom: 2rem;

  &::after {
    position: absolute;
    bottom: -2rem;
    right: 0;
    left: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      #c8d0d8 45%,
      #c8d0d8 55%,
      transparent
    );
    content: '';
  }
`

const SaveSearchBottom = styled.p`
  position: relative;
  text-align: center;
  margin-top: 4rem;

  &::after {
    position: absolute;
    top: -2rem;
    right: 0;
    left: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      #c8d0d8 45%,
      #c8d0d8 55%,
      transparent
    );
    content: '';
  }
`

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`

const SearchResultsWrapper = styled.div`
  position: relative;
  margin-bottom: 1rem;
`

const SearchResultList = styled.ul`
  position: absolute;
  bottom: 0;
  left: 0;
  transform: translateY(100%);
  padding: 0;
  margin: 0;
  background: white;
  border-radius: 4px;
  list-style-type: none;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.2);
`

const SearchResultItem = styled.li`
  color: rgb(120, 125, 130);
  padding: 4px 8px;

  &:hover,
  &:focus {
    background: rgb(220, 225, 230);
    color: #014c8c;
    cursor: pointer;
  }
`

type Props = {
  user: User,
  router: Object,
  alert: Function,
  results: number,
  location: Object,
  isLoading: boolean,
  isAuthenticated: boolean,
  fetchProperties: Function,
  properties: Array<Object>
}

type State = {
  limit: number,
  offset: number,
  q: string,
  sort: string,
  minprice: number,
  maxprice: number | '',
  minarea: number,
  minbeds: number,
  minbaths: number,
  counties: string,
  cities: string,
  neighborhoods: string,
  type: string,
  alertFormModalOpen: boolean
}

const defaults = {
  limit: 20,
  offset: 0,
  q: '',
  sort: '-listdate',
  minprice: 0,
  maxprice: '',
  minarea: 0,
  minbeds: 0,
  minbaths: 0,
  counties: '',
  cities: '',
  neighborhoods: '',
  type: ''
}

class Search extends Component {
  constructor(props: Props) {
    super(props)
    this.brokerId = get(props, 'location.query.broker')
    this.state = {
      limit: defaults.limit,
      offset: Number(get(props, 'location.query.offset', defaults.offset)),
      q: get(props, 'location.query.q', defaults.q),
      sort: get(props, 'location.query.sort', defaults.sort),
      minprice: get(props, 'location.query.minprice', defaults.minprice),
      maxprice: get(props, 'location.query.maxprice', defaults.maxprice),
      minarea: get(props, 'location.query.minarea', defaults.minarea),
      minbeds: get(props, 'location.query.minbeds', defaults.minbeds),
      minbaths: get(props, 'location.query.minbaths', defaults.minbaths),
      counties: get(props, 'location.query.counties', defaults.counties),
      cities: get(props, 'location.query.cities', defaults.cities),
      neighborhoods: get(
        props,
        'location.query.neighborhoods',
        defaults.neighborhoods
      ),
      type: get(props, 'location.query.type', defaults.type),
      alertFormModalOpen: false,
      searchResults: []
    }
    this.debouncedHandleSearchInput = debounce(this.handleSearchInput, 300)
  }

  state: State

  componentDidMount() {
    this.fetchProperties()
  }

  componentWillReceiveProps(newProps) {
    if (newProps.location.query.q !== this.props.location.query.q) {
      this.searchInput.value = newProps.location.query.q || ''
      this.setState({ q: newProps.location.query.q }, this.fetchProperties)
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.query.broker !== prevProps.location.query.broker) {
      this.brokerId = this.props.location.query.broker
      this.setState({ ...defaults }, this.fetchProperties)
    }
  }

  props: Props
  searchInput: HTMLInputElement

  fetchProperties = () => {
    this.props.fetchProperties({
      limit: this.state.limit,
      offset: this.state.offset,
      sort: this.state.sort,
      q: this.state.q,
      minprice: this.state.minprice,
      maxprice: this.state.maxprice,
      minarea: this.state.minarea,
      minbeds: this.state.minbeds,
      minbaths: this.state.minbaths,
      counties: this.state.counties,
      cities: this.state.cities,
      neighborhoods: this.state.neighborhoods,
      type: this.state.type,
      broker: this.brokerId
    })
  }

  handleParamUpdate = (param, value) => {
    this.props.router.replace({
      pathname: '/properties',
      query: { ...this.props.router.location.query, [param]: value }
    })
  }

  handleSearchInput = (text) => {
    if (!text) {
      this.setState({ searchResults: [] })
      return
    }

    const service = new window.google.maps.places.AutocompleteService()

    service.getQueryPredictions({ input: text }, (predictions, status) => {
      if (
        status != window.google.maps.places.PlacesServiceStatus.OK ||
        !predictions
      ) {
        this.setState({ searchResults: [] })
        return
      }

      this.setState({ searchResults: predictions })
    })
  }

  handleSearchResultSelect = (prediction) => {
    const city = prediction.structured_formatting.main_text
    this.searchInput.value = city
    this.handleParamUpdate('q', city)
    this.handleParamUpdate('offset', 0)
    this.setState(
      { q: city, offset: 0, searchResults: [] },
      this.fetchProperties
    )
  }

  handleFilterChange = (param: string, value: string | number) => {
    this.handleParamUpdate(param, value)
    this.handleParamUpdate('offset', 0)
    this.setState({ [param]: value, offset: 0 }, this.fetchProperties)
  }

  handlePrevPageClick = () => {
    const offset = this.state.offset - this.state.limit
    this.handleParamUpdate('offset', offset)
    this.setState({ offset }, this.fetchProperties)
  }

  handleNextPageClick = () => {
    const offset = this.state.offset + this.state.limit
    this.handleParamUpdate('offset', offset)
    this.setState({ offset }, this.fetchProperties)
  }

  clearFilters = () => {
    this.searchInput.value = ''
    const newQuery = {}
    if (this.brokerId) {
      // Keep broker if it was in the initial params
      // for Featured Listings page
      newQuery.broker = this.brokerId
    }
    this.props.router.replace({ pathname: '/properties', query: newQuery })
    this.setState({ ...defaults }, this.fetchProperties)
  }

  handleAlertButtonClick = () => {
    if (this.props.isAuthenticated) {
      this.setState({ alertFormModalOpen: true })
    } else {
      this.props.alert({
        message: 'Please login to save searches',
        klass: 'primary',
        timeout: 4000
      })
    }
  }

  render() {
    const {
      minprice,
      maxprice,
      minbeds,
      minbaths,
      minarea,
      sort
      // type
    } = this.state

    return (
      <div>
        <Helmet title="Search Homes for Sale in Champaign Illinois" />
        <FiltersContainer>
          <div className="container">
            {this.brokerId && <Header>Featured Listings</Header>}
            <SearchResultsWrapper>
              <input
                className="form-control"
                defaultValue={this.state.q}
                placeholder="Where do you want to live?"
                ref={(node) => {
                  this.searchInput = node
                }}
                onKeyUp={(event) => {
                  const value = event.target.value
                  this.debouncedHandleSearchInput(value)
                }}
              />
              <SearchResultList>
                {this.state.searchResults.map((prediction) => (
                  <SearchResultItem
                    key={prediction.place_id}
                    onClick={() => this.handleSearchResultSelect(prediction)}
                  >
                    {prediction.description}
                  </SearchResultItem>
                ))}
              </SearchResultList>
            </SearchResultsWrapper>
            <FiltersRow>
              <Filter
                value={minprice}
                param="minprice"
                options={minPriceOptions}
                onChange={this.handleFilterChange}
              />
              <Filter
                value={maxprice}
                param="maxprice"
                options={maxPriceOptions}
                onChange={this.handleFilterChange}
              />
              <Filter
                value={minbeds}
                param="minbeds"
                options={minBedOptions}
                onChange={this.handleFilterChange}
              />
              <Filter
                value={minbaths}
                param="minbaths"
                options={minBathOptions}
                onChange={this.handleFilterChange}
              />
              <Filter
                value={minarea}
                param="minarea"
                options={minAreaOptions}
                onChange={this.handleFilterChange}
              />
              {/* <Filter
                value={type}
                param="type"
                options={typeOptions}
                onChange={this.handleFilterChange}
              /> */}
              <Filter
                value={sort}
                param="sort"
                options={sortOptions}
                onChange={this.handleFilterChange}
              />
              <Button
                type="button"
                onClick={this.clearFilters}
                style={{ marginBottom: '1rem' }}
              >
                <span className="fa fa-close" style={{ marginRight: '5px' }} />
                <span>Clear filters</span>
              </Button>
            </FiltersRow>
          </div>
        </FiltersContainer>
        <ResultsContainer>
          {this.props.results > 0 && (
            <div className="container">
              <SaveSearchTop>
                <LinkButton onClick={this.handleAlertButtonClick}>
                  Save this search
                </LinkButton>{' '}
                to be notified when properties hit the market.
              </SaveSearchTop>
            </div>
          )}
          {this.props.results > 0 && (
            <Pager
              limit={this.state.limit}
              offset={this.state.offset}
              results={this.props.results}
              style={{ margin: '2rem 0' }}
              onPrevPageClick={this.handlePrevPageClick}
              onNextPageClick={this.handleNextPageClick}
            />
          )}
          <Results>
            {this.props.properties.map((property) => (
              <SearchResult key={property.mlsId} property={property} />
            ))}
            {this.props.isLoading &&
              Array(this.state.limit)
                .fill()
                .map((_, i) => <LoadingResult key={i} />)}
          </Results>
          {this.props.results > 0 && (
            <Pager
              limit={this.state.limit}
              offset={this.state.offset}
              results={this.props.results}
              style={{ margin: '2rem 0' }}
              onPrevPageClick={this.handlePrevPageClick}
              onNextPageClick={this.handleNextPageClick}
            />
          )}
          {this.props.results > 0 && (
            <div className="container">
              <SaveSearchBottom>
                <LinkButton onClick={this.handleAlertButtonClick}>
                  Save this search
                </LinkButton>{' '}
                to be notified when properties hit the market.
              </SaveSearchBottom>
            </div>
          )}
          {this.props.isLoading && <LoadingOverlay />}
          {!this.props.isLoading && !this.props.properties.length && (
            <NoResults />
          )}
        </ResultsContainer>
        <AlertFormModal
          user={this.props.user}
          isOpen={this.state.alertFormModalOpen}
          onRequestClose={() => {
            this.setState({ alertFormModalOpen: false })
          }}
          initialValues={{
            keyword: this.state.q,
            minprice: this.state.minprice,
            maxprice: this.state.maxprice,
            minbeds: this.state.minbeds,
            minbaths: this.state.minbaths,
            minarea: this.state.minarea
          }}
        />
      </div>
    )
  }
}

export default connect(
  (state) => ({
    user: state.session.currentUser,
    properties: state.properties.list,
    isLoading: state.properties.isLoadingList,
    results: state.properties.results,
    isAuthenticated: state.session.isAuthenticated
  }),
  {
    alert,
    fetchProperties
  }
)(Search)



// WEBPACK FOOTER //
// ./src/views/Search/index.js